import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Search, CheckCircle, XCircle, Clock, Edit, X, Save } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: "bg-amber-50 text-amber-700",
  approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
};

const activityLabels = {
  volunteer: "Volunteering",
  training: "Training",
  community_service: "Community Service",
  mentoring: "Mentoring",
  other: "Other",
};

export const HoursPanel = ({ getAuthHeader }) => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [editingLog, setEditingLog] = useState(null);
  const [editForm, setEditForm] = useState({ hours: "", date: "", activity_type: "", description: "" });

  const fetchData = async () => {
    try {
      const [logsRes, statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/hours`, getAuthHeader()),
        axios.get(`${API}/admin/hours/stats`, getAuthHeader()),
        axios.get(`${API}/admin/users`, getAuthHeader()),
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setLoaded(true);
    } catch { toast.error("Failed to load hours data"); }
  };

  if (!loaded) fetchData();

  const handleStatus = async (logId, status) => {
    try {
      await axios.put(`${API}/admin/hours/${logId}/status`, { status }, getAuthHeader());
      toast.success(`Hours ${status}`);
      fetchData();
    } catch { toast.error("Failed to update"); }
  };

  const handleToggleUser = async (userId) => {
    try {
      const res = await axios.put(`${API}/admin/users/${userId}/status`, {}, getAuthHeader());
      toast.success(`User ${res.data.status}`);
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const startEdit = (log) => {
    setEditingLog(log.id);
    setEditForm({
      hours: String(log.hours),
      date: log.date,
      activity_type: log.activity_type,
      description: log.description || "",
    });
  };

  const cancelEdit = () => {
    setEditingLog(null);
    setEditForm({ hours: "", date: "", activity_type: "", description: "" });
  };

  const saveEdit = async (logId) => {
    try {
      const payload = {};
      if (editForm.hours) payload.hours = parseFloat(editForm.hours);
      if (editForm.date) payload.date = editForm.date;
      if (editForm.activity_type) payload.activity_type = editForm.activity_type;
      if (editForm.description !== undefined) payload.description = editForm.description;
      await axios.put(`${API}/admin/hours/${logId}/edit`, payload, getAuthHeader());
      toast.success("Hour log updated");
      cancelEdit();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const filtered = logs.filter(l =>
    `${l.user_name} ${l.activity_type} ${l.description} ${l.date}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-amber-50 rounded-xl p-4">
            <p className="text-xs text-amber-600 font-medium">Pending Approval</p>
            <p className="text-2xl font-bold text-amber-700">{stats.total_pending}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4">
            <p className="text-xs text-emerald-600 font-medium">Approved Entries</p>
            <p className="text-2xl font-bold text-emerald-700">{stats.total_approved}</p>
          </div>
          <div className="bg-warm-50 rounded-xl p-4">
            <p className="text-xs text-warm-600 font-medium">Total Approved Hours</p>
            <p className="text-2xl font-bold text-warm-700">{stats.total_approved_hours}h</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4">
            <p className="text-xs text-blue-600 font-medium">Registered Users</p>
            <p className="text-2xl font-bold text-blue-700">{users.length}</p>
          </div>
        </div>
      )}

      {/* Users Section */}
      {users.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Registered Users</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-100">
                <th className="text-left py-2 px-3 text-gray-500 font-medium">Name</th>
                <th className="text-left py-2 px-3 text-gray-500 font-medium hidden sm:table-cell">Email</th>
                <th className="text-left py-2 px-3 text-gray-500 font-medium">Role</th>
                <th className="text-left py-2 px-3 text-gray-500 font-medium">Status</th>
                <th className="text-right py-2 px-3 text-gray-500 font-medium">Action</th>
              </tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-gray-50">
                    <td className="py-2 px-3 font-medium">{u.name}</td>
                    <td className="py-2 px-3 text-gray-500 hidden sm:table-cell">{u.email}</td>
                    <td className="py-2 px-3 capitalize text-gray-600">{u.role}</td>
                    <td className="py-2 px-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{u.status}</span>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleToggleUser(u.id)} className="text-xs">
                        {u.status === "active" ? "Deactivate" : "Activate"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Hour Logs */}
      <div>
        <div className="flex items-center justify-between gap-4 mb-3">
          <h4 className="text-sm font-semibold text-gray-700">Hour Logs</h4>
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search logs..." className="pl-10" />
          </div>
        </div>
        <div className="overflow-x-auto bg-white rounded-xl border border-gray-100">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-100">
              <th className="text-left py-3 px-4 text-gray-500 font-medium">User</th>
              <th className="text-left py-3 px-4 text-gray-500 font-medium">Date</th>
              <th className="text-left py-3 px-4 text-gray-500 font-medium hidden md:table-cell">Activity</th>
              <th className="text-right py-3 px-4 text-gray-500 font-medium">Hours</th>
              <th className="text-left py-3 px-4 text-gray-500 font-medium">Status</th>
              <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map(l => (
                editingLog === l.id ? (
                  <tr key={l.id} className="border-b border-gray-50 bg-warm-50/30">
                    <td className="py-3 px-4">
                      <p className="font-medium text-gray-900 text-sm">{l.user_name}</p>
                    </td>
                    <td className="py-3 px-4">
                      <Input data-testid="edit-hour-date" type="date" value={editForm.date} onChange={e => setEditForm({ ...editForm, date: e.target.value })} className="h-8 text-sm w-36" />
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell">
                      <select data-testid="edit-hour-activity" value={editForm.activity_type} onChange={e => setEditForm({ ...editForm, activity_type: e.target.value })} className="h-8 border rounded-lg px-2 text-sm">
                        <option value="volunteer">Volunteering</option>
                        <option value="training">Training</option>
                        <option value="community_service">Community Service</option>
                        <option value="mentoring">Mentoring</option>
                        <option value="other">Other</option>
                      </select>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Input data-testid="edit-hour-amount" type="number" step="0.5" min="0.5" max="24" value={editForm.hours} onChange={e => setEditForm({ ...editForm, hours: e.target.value })} className="h-8 text-sm w-20 text-right" />
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColors[l.status]}`}>{l.status}</span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" data-testid="edit-hour-save" onClick={() => saveEdit(l.id)} className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700" title="Save">
                          <Save className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={cancelEdit} className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600" title="Cancel">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                <tr key={l.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-3 px-4">
                    <p className="font-medium text-gray-900">{l.user_name}</p>
                    {l.description && <p className="text-xs text-gray-400 truncate max-w-[200px]">{l.description}</p>}
                  </td>
                  <td className="py-3 px-4 text-gray-600">{l.date}</td>
                  <td className="py-3 px-4 text-gray-600 hidden md:table-cell">{activityLabels[l.activity_type] || l.activity_type}</td>
                  <td className="py-3 px-4 text-right font-medium">{l.hours}h</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColors[l.status]}`}>{l.status}</span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => startEdit(l)} className="h-8 w-8 p-0 text-gray-400 hover:text-warm-700" title="Edit" data-testid={`edit-hour-${l.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      {l.status === "pending" && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => handleStatus(l.id, "approved")} className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700" title="Approve">
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleStatus(l.id, "rejected")} className="h-8 w-8 p-0 text-red-500 hover:text-red-700" title="Reject">
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                )
              ))}
              {filtered.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-gray-400">No hour logs</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
